import { useCallback, useEffect, useRef, useState } from 'react';

/** How near the bottom still counts as being at the bottom (D-79). */
const NEAR = 80;

/**
 * Keeps a conversation on its newest line while an answer streams, the way
 * chat apps do (D-79). It follows only while the reader is at the bottom;
 * scrolling up stops it at once and keeps the place, and `follow()` (the
 * "↓ En alta in" button, a new message) resumes it, as does scrolling back
 * to within 80 px of the bottom. The area itself scrolls (`scrollTop`,
 * never `scrollIntoView`, so the page and outer boxes stay put), at most
 * once per frame and without animation: growth is seen by a ResizeObserver,
 * which reports once per frame, so tokens, tables, code, images and cards
 * all count.
 */
export function useFollowBottom(resetKey: string) {
  const box = useRef<HTMLElement | null>(null);
  const [scroller, setScroller] = useState<HTMLElement | null>(null);
  const [content, setContent] = useState<HTMLElement | null>(null);
  const following = useRef(true);
  const lastTop = useRef(0);
  const [away, setAway] = useState(false);

  const scrollerRef = useCallback((el: HTMLElement | null) => {
    box.current = el;
    setScroller(el);
  }, []);

  const pin = useCallback(() => {
    const el = box.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight - el.clientHeight;
    lastTop.current = el.scrollTop;
  }, []);

  const follow = useCallback(() => {
    following.current = true;
    setAway(false);
    pin();
  }, [pin]);

  useEffect(() => {
    if (!scroller || !content) return;
    const el = scroller;
    following.current = true;
    setAway(false);
    const gap = () => el.scrollHeight - el.scrollTop - el.clientHeight;
    const check = () => setAway(!following.current && gap() > NEAR);
    const stop = () => {
      following.current = false;
      check();
    };
    // a ResizeObserver reports once per frame, after layout and before paint: the new line never shows cut off
    const onResize = () => {
      if (following.current) pin();
      check();
    };
    const onScroll = () => {
      const top = el.scrollTop;
      // a move up stops following; a box that shrank lands on the bottom, which is not a move
      if (top < lastTop.current - 1 && gap() > 1) following.current = false;
      else if (top > lastTop.current && gap() <= NEAR) following.current = true;
      lastTop.current = top;
      check();
    };
    // the reader's own hand, before the scroll it starts: a wheel or a finger moving up, the scrollbar
    const onWheel = (event: WheelEvent) => {
      if (event.deltaY < 0 && !event.ctrlKey) stop();
    };
    let touchY: number | null = null;
    const onTouchStart = (event: TouchEvent) => {
      touchY = event.touches[0]?.clientY ?? null;
    };
    const onTouchMove = (event: TouchEvent) => {
      const y = event.touches[0]?.clientY;
      if (touchY !== null && y !== undefined && y > touchY + 4) stop();
    };
    const onPointerDown = (event: PointerEvent) => {
      if (event.target === el) stop();
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    el.addEventListener('wheel', onWheel, { passive: true });
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });
    el.addEventListener('pointerdown', onPointerDown);
    const observer = new ResizeObserver(onResize);
    observer.observe(content);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', onScroll);
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('pointerdown', onPointerDown);
      observer.disconnect();
    };
  }, [scroller, content, pin, resetKey]);

  return { scrollerRef, contentRef: setContent, away, follow };
}
